/**
 * Shared types for Client Workspace components.
 */

import { RoleName } from "constants";

// ── Role-based helpers ──

export type ClientUserRole = "admin" | "team-member" | "team-viewer" | "viewer" | "guest" | "investor";

const READ_ONLY_ROLE_NAMES: RoleName[] = [RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer];

export function isReadOnlyRole(roleName: string | null | undefined): boolean {
  return Boolean(roleName && READ_ONLY_ROLE_NAMES.includes(roleName as RoleName));
}

export function isTeamViewerRole(roleName: string | null | undefined): boolean {
  return roleName === RoleName.TeamViewer;
}

// ── View types ──

export interface ActiveViewState {
  activeViewType: "smart_view" | "folder";
  activeViewId: string;
}

export type SmartViewType = "all_files" | "recently_added" | "email";

export interface SmartViewMenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

// ── Folder types ──

export interface ClientWorkspaceFolder {
  id: number;
  client_id?: number;
  name: string;
  parent_id: number | null;
  children?: ClientWorkspaceFolder[];
  file_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface FolderNode {
  id: string;
  name: string;
  children?: FolderNode[];
}

// ── File types ──

export type ClientWorkspaceFileSourceType = "client" | "deal";

export interface ClientWorkspaceFile {
  id: number;
  name: string;
  size_bytes?: number;
  mime_type?: string;
  classification: string | null;
  status: string | null;
  tags?: unknown[];
  source: ClientWorkspaceFileSourceType;
  folder_id: number | null;
  folder_name?: string | null;
  deal_id: number | null;
  deal_name: string | null;
  investor_id?: number | null;
  investor_name?: string | null;
  uploaded_by?: string;
  created_at: string | null;
  updated_at?: string | null;
  s3_key?: string;
  path?: string;
}

export interface AllFilesResponse {
  items: ClientWorkspaceFile[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface AllFilesApiResponse {
  data: AllFilesResponse;
}

// ── Upload types ──

export interface UploadFileItem {
  file: File;
  metadata: {
    name: string;
    classification: string;
    status?: string;
    tags?: string[];
  };
}

export interface UploadResultItem {
  file_index: number;
  status: "success" | "error";
  file?: ClientWorkspaceFile;
  error?: string;
}

export interface UploadApiResponse {
  status: number;
  data: ClientWorkspaceFile[] | UploadResultItem[];
}

// ── Source display ──

export const SOURCE_LABELS: Record<ClientWorkspaceFileSourceType, string> = {
  client: "Client Workspace",
  deal: "Deal",
};

export const SOURCE_COLUMN_KEY = "source";

// ── Misc ──

export interface SmartFolderDeleteConfirmData {
  folderId: number;
  fileName: string;
  fileCount: number;
}

export function isDealSourcedRow(row: Record<string, unknown>): boolean {
  return (row.source as string) === "Deal";
}

// ── AllFilesParams ──

export interface AllFilesParams {
  page?: number;
  per_page?: number;
  source?: "all" | "client" | "deal";
  folder_id?: number;
  search?: string;
  classification?: string;
  sort_by?: string;
  sort?: string;
}

export interface UpdateFilePayload {
  name?: string;
  classification?: string;
  status?: string;
  tags?: string[];
}
